import React from "react";
import { useI18n, isBCPLocale } from "@/utils/rioti18n";
import logger from "@/utils/logger";
import { safeSessionStorage } from "@/utils/safeStorages";
import siteConfig from "@/variables";

type LocaleSwitcherProviderProps = {
  children?: React.ReactNode;
};

// This is purposefully not a hook so this function can be used outside of React.Components
export const getCurrentLocale = (fullPath: string) => {
  const currentLocale = fullPath.split("/")[1];

  if (currentLocale.includes("-") && isBCPLocale(currentLocale)) {
    return currentLocale;
  }
  return "";
};

/*
  This provider acts as a template wrapper, and allows us to add functionality to Riotbar
  that we would not normally be able to have.
*/
export const LocaleSwitcherProvider = ({ children }: LocaleSwitcherProviderProps) => {
  const { language, changeLanguage } = useI18n();
  const [mutationObserver, setMutationObserver] = React.useState<undefined | MutationObserver>();

  const setSelectedRiotbarLocale = (language: string) => {
    const localeLists = document.querySelectorAll(".locale-list");
    let svgNode: Node;
    Array.from(localeLists).forEach((localeList) => {
      const lis = localeList.querySelectorAll("li");
      Array.from(lis).forEach((li) => {
        const link = li.querySelectorAll("a")[0];
        const linkLocaleTestId = link.getAttribute("data-testid");
        const linkLocale = linkLocaleTestId?.substring(linkLocaleTestId.length - 5);
        if (linkLocale !== language && li.classList.contains("active-language")) {
          li.classList.remove("active-language");
          const svg = li.querySelectorAll("svg")[0];
          svgNode = svg.cloneNode(true);
          if (svg.parentNode) {
            svg.parentNode.removeChild(svg);
          }
        }
      });
    });

    Array.from(localeLists).forEach((localeList) => {
      const lis = localeList.querySelectorAll("li");
      Array.from(lis).forEach((li) => {
        const link = li.querySelectorAll("a")[0];
        const linkLocaleTestId = link.getAttribute("data-testid");
        const linkLocale = linkLocaleTestId?.substring(linkLocaleTestId.length - 5);
        if (linkLocale === language) {
          li.classList.add("active-language");
          li.appendChild(svgNode);
        }
      });
    });
  };

  // updates RiotBar's language selector dropdown links to preserve the current route
  const updateLocaleSwitcherURLS = React.useCallback(() => {
    // Gets the element that contains locale switcher:
    const hrefEls = document.querySelectorAll(".locale-list li a");
    Array.from(hrefEls).forEach((el) => {
      /*
      Get the locale from data-testid attribute, we strip the last 5 characters as we expect
      the format to be along the lines of "riotbar:localeswitcher:link-en-us"
      */
      const dataTestId = el.getAttribute("data-testid");
      const locale = dataTestId?.substring(dataTestId.length - 5);
      if (!locale || !isBCPLocale(locale)) {
        logger("Unable to setup redirect, got null locale", undefined, "error", { el });
      } else {
        el.setAttribute("href", "#");
        el.addEventListener("click", (e) => {
          e.preventDefault();

          // This is necessary to clean up rewards info from Summary and Success page,
          // The data we present is based on the results of validation endpoint and then
          // stored in the sessionStorage. If we don't clean it up and force a refresh,
          // the content will not update translations
          safeSessionStorage.removeItem(siteConfig.KEY_REDEEM_REWARDS);

          changeLanguage(locale);
        });
      }
    });
  }, []);

  // On Page load, check to see if the correct locale version of Riotbar was loaded
  React.useEffect(() => {
    const script = document.getElementById("riotbar-script") as unknown as HTMLScriptElement;
    if (!script) {
      return;
    }
    const src = script.src;
    // Src pattern looks like: https://cdn.rgpub.io/private/test/riotbar/master/shop-riotgames-com-riot.pt-br.js
    const locale = src.substring(src.length - 8).substring(0, 5);
    if (isBCPLocale(locale) && isBCPLocale(language) && locale !== language) {
      window.location.reload();
    }
  }, []);

  React.useEffect(() => {
    setMutationObserver(
      new MutationObserver(() => {
        updateLocaleSwitcherURLS();
      }),
    );
  }, []);

  React.useEffect(() => {
    setSelectedRiotbarLocale(language);
  }, [language]);

  React.useEffect(() => {
    mutationObserver?.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
    // Remove on unmount
    return () => mutationObserver?.disconnect();
  }, [mutationObserver]);

  return <>{children}</>;
};

export default LocaleSwitcherProvider;
